import { Component } from '@theme/component';
import {
  getScrollEventTarget,
  getScrollTop,
  scrollTo,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';

const DEFAULT_THRESHOLD = 480;

/**
 * @typedef {Object} ScrollToTopRefs
 * @property {HTMLButtonElement} button
 */

/**
 * Floating control that scrolls the page container back to the top.
 * @extends {Component<ScrollToTopRefs>}
 */
class ScrollToTop extends Component {
  /** @type {EventTarget | null} */
  #scrollTarget = null;

  /** @type {number | null} */
  #rafId = null;

  connectedCallback() {
    super.connectedCallback();
    this.#bindScrollTarget();
    scrollContainerMediaQuery.addEventListener('change', this.#handleMediaChange);
    this.refs.button?.addEventListener('click', this.#handleClick);
    this.#updateVisibility();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#unbindScrollTarget();
    scrollContainerMediaQuery.removeEventListener('change', this.#handleMediaChange);
    this.refs.button?.removeEventListener('click', this.#handleClick);
    if (this.#rafId != null) cancelAnimationFrame(this.#rafId);
  }

  get #threshold() {
    const value = Number(this.dataset.threshold);
    return Number.isFinite(value) && value > 0 ? value : DEFAULT_THRESHOLD;
  }

  #bindScrollTarget() {
    this.#unbindScrollTarget();
    this.#scrollTarget = getScrollEventTarget();
    this.#scrollTarget.addEventListener('scroll', this.#handleScroll, { passive: true });
  }

  #unbindScrollTarget() {
    this.#scrollTarget?.removeEventListener('scroll', this.#handleScroll);
    this.#scrollTarget = null;
  }

  #handleMediaChange = () => {
    this.#bindScrollTarget();
    this.#updateVisibility();
  };

  #handleScroll = () => {
    if (this.#rafId != null) return;
    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = null;
      this.#updateVisibility();
    });
  };

  #updateVisibility() {
    const isVisible = getScrollTop() >= this.#threshold;
    const button = this.refs.button;

    this.classList.toggle('is-visible', isVisible);
    this.toggleAttribute('data-visible', isVisible);
    this.setAttribute('aria-hidden', String(!isVisible));

    if (button instanceof HTMLButtonElement) {
      button.tabIndex = isVisible ? 0 : -1;
      button.setAttribute('aria-hidden', String(!isVisible));
    }
  }

  #handleClick = () => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    scrollTo({ top: 0, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
  };
}

if (!customElements.get('scroll-to-top')) {
  customElements.define('scroll-to-top', ScrollToTop);
}
